import { useEffect, useState } from 'react';
import { Check, Landmark } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { fmtDateTimeEastern } from '../lib/format';

type Donde = 'portal' | 'email_other' | 'fax' | 'mail' | 'phone' | 'other';
type Estado = { lor_sent_at: string | null; filed_at: string | null; sends: Array<{ at: string; by_name: string | null; manual: boolean; nota: string | null }> };

/**
 * CONFIRMAR LA LOR DESDE SCHEDULING (dueño 2026-10-14: "a button to confirm that the LOR has been sent to the
 * insurance, under Appointments, so we can confirm it from our section"). Es el mismo camino que el cuadro "la LOR
 * salió por otro sistema" de Ready to be filed: pregunta DÓNDE se mandó, la sella con el nombre de quien confirma y
 * mueve el claim a Claim in en el mismo paso. Si ya está enviada, solo lo dice (cuándo y quién).
 */
export function LorConfirmButton({ claimId, onChanged }: { claimId: string; onChanged?: () => void }) {
  const { t, lang } = useI18n();
  const [st, setSt] = useState<Estado | null>(null);
  const [open, setOpen] = useState(false);
  const [where, setWhere] = useState<Donde>('portal');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = () => api.get<Estado>(`/api/claims/${claimId}/lor`).then(setSt).catch(() => setSt(null));
  useEffect(() => { void load(); }, [claimId]); // eslint-disable-line react-hooks/exhaustive-deps

  const DONDE: Array<{ key: Donde; label: string }> = [
    { key: 'portal', label: t('lor.extPortal') }, { key: 'email_other', label: t('lor.extEmailOther') }, { key: 'fax', label: t('lor.extFax') },
    { key: 'mail', label: t('lor.extMail') }, { key: 'phone', label: t('lor.extPhone') }, { key: 'other', label: t('lor.extOther') },
  ];
  const errores: Record<string, string> = {
    claim_number_required: t('lor.errClaimNumber'), claim_type_required: t('rtf.errType'), lor_required: t('lor.errLorRequired'), not_ready_to_file: t('lor.confirmNotReady'),
  };

  async function confirmar() {
    const d = detail.trim();
    if (!d) { setMsg({ ok: false, text: t('lor.extDetailRequired') }); return; }
    setBusy(true); setMsg(null);
    try {
      const donde = DONDE.find((x) => x.key === where)?.label ?? where;
      const r = await api.post<{ filed: boolean; filing_blocked: string | null }>(`/api/claims/${claimId}/lor/mark-sent`, { note: `${donde}: ${d}`, where, file: true });
      setOpen(false); setDetail('');
      await load();
      if (r.filed) setMsg({ ok: true, text: t('lor.extFiled') });
      else setMsg({ ok: true, text: `${t('lor.confirmSentOnly')}${r.filing_blocked ? ` ${errores[r.filing_blocked] || r.filing_blocked}` : ''}` });
      onChanged?.();
    } catch (e) { const raw = e instanceof ApiError ? e.message : 'Error'; setMsg({ ok: false, text: errores[raw] || raw }); }
    finally { setBusy(false); }
  }

  if (!st) return null;
  const ultimo = st.sends.length ? st.sends[st.sends.length - 1]! : null;
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)', display: 'grid', gap: 6, fontSize: 'var(--fs-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], textTransform: 'uppercase', letterSpacing: 0.4 }}>
          <Landmark size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('lor.title')}
        </span>
        {ultimo ? (
          <span style={{ color: 'var(--green)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>
            <Check size={13} strokeWidth={2.2} style={{ verticalAlign: 'text-bottom' }} /> {t('lor.confirmSent')} · {fmtDateTimeEastern(ultimo.at, lang)}{ultimo.by_name ? ` · ${ultimo.by_name}` : ''}{ultimo.manual && ultimo.nota ? ` · ${ultimo.nota}` : ''}
          </span>
        ) : (
          <button className="btn sm" disabled={busy} onClick={() => setOpen((o) => !o)}>
            <Check size={13} strokeWidth={2} /> {t('lor.confirmBtn')}
          </button>
        )}
      </div>
      {open && !ultimo && (
        <div style={{ display: 'grid', gap: 6, padding: '8px 10px', border: '1px dashed var(--line)', borderRadius: 8, background: 'var(--surface-2)' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'end' }}>
            <label style={{ display: 'grid', gap: 3 }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('lor.extWhere')}</span>
              <select className="input sm" value={where} onChange={(e) => setWhere(e.target.value as Donde)}>
                {DONDE.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 3, flex: 1, minWidth: 220 }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('lor.extDetail')}</span>
              <input className="input sm" value={detail} placeholder={t('lor.extDetailPh')} onChange={(e) => setDetail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }} />
            </label>
            <button className="btn sm solid-good" disabled={busy} onClick={confirmar}><Check size={13} strokeWidth={2} /> {t('lor.extConfirm')}</button>
            <button className="btn ghost sm" disabled={busy} onClick={() => setOpen(false)}>{t('common.cancel')}</button>
          </div>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('lor.extHint')}</div>
        </div>
      )}
      {msg && <div style={{ color: msg.ok ? 'var(--green)' : 'var(--red)', fontSize: 'var(--fs-xs)' }}>{msg.text}</div>}
    </div>
  );
}
